import axios from 'axios';
import ApiClient from './api_client';

// Note that cts is short for cancel token source,
// which is too long to write a lot.

const QUERY_ITEMS_BY_IDS_BATCH_SIZE = 100;
const QUERY_ITEMS_BY_IDS_MAX_CONCURRENCY = 3;

function chunk(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
}

function mapWithConcurrency(items, maxConcurrency, mapper) {
  return new Promise((resolve, reject) => {
    const results = new Array(items.length);
    let activeCount = 0;
    let completedCount = 0;
    let nextIndex = 0;
    let stopped = false;

    const startNext = () => {
      if (stopped) return;
      if (completedCount === items.length) {
        resolve(results);
        return;
      }

      while (activeCount < maxConcurrency && nextIndex < items.length) {
        const currentIndex = nextIndex++;
        activeCount++;

        let promise;
        try {
          promise = mapper(items[currentIndex], currentIndex);
        } catch (error) {
          stopped = true;
          reject(error);
          return;
        }

        Promise.resolve(promise).then(result => {
          if (stopped) return;
          results[currentIndex] = result;
          activeCount--;
          completedCount++;
          startNext();
        }).catch(error => {
          if (stopped) return;
          stopped = true;
          reject(error);
        });
      }
    };

    startNext();
  });
}

function _get(endpoint, params, cts) { // eslint-disable-line no-unused-vars
  const config = {};
  if (cts) config.cancelToken = cts.token;
  if (params) config.params = params;
  return axios.get(process.env.REACT_APP_API_BASE_URL + endpoint, config);
}

function _post(endpoint, data, cts, headers = null) {
  const config = {};
  if (cts) config.cancelToken = cts.token;
  if (headers) config.headers = headers;
  if (!data) data = new FormData();
  return axios.post(process.env.REACT_APP_API_BASE_URL + endpoint, data, config);
}

function removeAuthHeader(data, headers) {
  delete headers.common.Authorization;
  return data;
}

// The search service is a separate, public origin: sending these would force a CORS preflight it does not allow.
function removeSearchServiceHeaders(data, headers) {
  delete headers.common.Authorization;
  delete headers.common['X-Lem-FallbackWebSessionID'];
  return data;
}

function _getWithNoAuth(endpoint, params, cts) {
  const config = { transformRequest: [removeAuthHeader] };
  if (cts) config.cancelToken = cts.token;
  if (params) config.params = params;
  return axios.get(process.env.REACT_APP_API_BASE_URL + endpoint, config);
}

function _postWithNoAuth(endpoint, data, cts, headers = null) {
  const config = { transformRequest: [removeAuthHeader] };
  if (cts) config.cancelToken = cts.token;
  if (headers) config.headers = headers;
  if (!data) data = new FormData();
  return axios.post(process.env.REACT_APP_API_BASE_URL + endpoint, data, config);
}

function _crossOriginGet(endpoint, params, cts) { // eslint-disable-line no-unused-vars
  return _getWithNoAuth(endpoint, params, cts);
}

function _crossOriginPost(endpoint, data, cts) { // eslint-disable-line no-unused-vars
  return _postWithNoAuth(endpoint, data, cts);
}

class WebApiClient extends ApiClient {
  getCurrentUser(cts) {
    return _post('/api/me', new FormData(), cts);
  }

  forgotPassword(email, recaptchaToken, cts) {
    const data = new FormData();
    data.append('email', email);
    data.append('recaptcha_token', recaptchaToken);
    return _post('/api/forgot_password', data, cts);
  }

  startMagicLink(email, subscribeMe = null, forceApp = false, forceWeb = true, recaptchaToken = null, cts) {
    const data = new FormData();
    data.append('email', email);
    if (subscribeMe != null) data.append('subscribe_me', subscribeMe ? 'true' : 'false');
    data.append('force_app', forceApp ? 'true' : 'false');
    data.append('force_web', forceWeb ? 'true' : 'false');
    if (recaptchaToken) data.append('recaptcha_token', recaptchaToken);
    return _post('/api/magic_link/start', data, cts);
  }

  checkPasswordResetCode(code, cts) {
    const data = new FormData();
    data.append('code', code);
    return _post('/api/check_password_reset_code', data, cts);
  }

  resetPassword(code, newPassword, cts) {
    const data = new FormData();
    data.append('code', code);
    data.append('new_password', newPassword);
    return _post('/api/reset_password', data, cts);
  }

  editUserProfileField(fieldName, value, cts) {
    const data = new FormData();
    data.append('field_name', fieldName);
    data.append('value', value);
    return _post('/api/user_profile_field_edit', data, cts);
  }

  uploadProfilePicture(fileUuid) {
    const data = new FormData();
    data.append('file_uuid', fileUuid);
    return _post('/api/upload_profile_picture_v2', data);
  }

  subscribe(email, name, subscribeGenderInterest, promotion, recaptchaToken, cts) {
    const data = new FormData();
    data.append('email', email);
    if (name) data.append('name', name);
    if (promotion) data.append('promotion', promotion);
    data.append('recaptcha_token', recaptchaToken);
    data.append('gender_preference', subscribeGenderInterest);
    return _post('/api/subscribe_newsletter', data, cts);
  }

  contactUs(name, subject, email, message, recaptchaToken, cts) {
    const data = new FormData();
    data.append('name', name);
    data.append('email', email);
    data.append('subject', subject);
    data.append('message', message);
    data.append('recaptcha_token', recaptchaToken);
    return _post('/api/contact_us', data, cts);
  }

  requestAQuote(email, itemId, recaptchaToken, cts) {
    const data = new FormData();
    data.append('email', email);
    data.append('item_id', itemId);
    data.append('recaptcha_token', recaptchaToken);
    return _post('/api/request_a_quote', data, cts);
  }

  getCampaign(campaignId, cts) {
    const data = new FormData();
    data.append('campaign_id', campaignId);
    return _post('/api/get_campaign', data, cts);
  }

  checkPromoCode(promoCode, cts) {
    const data = new FormData();
    data.append('promo_code', promoCode);
    return _post('/api/check_promo_code', data, cts);
  }

  clearPromoCode(cts) {
    return _post('/api/clear_promo_code', new FormData(), cts);
  }

  placeOrder(email, firstName, lastName, addressLine0, addressLine1, city, paymentMethod, countryCode, mobileNumber,
    recaptchaToken, serviceProviderKey, wantsToSubscribe, subscribeGenderInterest, intendedCartElementIds,
    stripePaymentMethod, shouldSaveStripePaymentMethod, cartId = null, idempotencyKey = null,
    savedAddressId = null, shippingMethodChoices = null, cts) {
    const data = new FormData();
    data.append('email', email);
    data.append('first_name', firstName);
    data.append('last_name', lastName);
    data.append('address_line_0', addressLine0);
    data.append('address_line_1', addressLine1);
    data.append('city', city);
    data.append('payment_method', paymentMethod);
    data.append('mobile_country_code', countryCode);
    data.append('mobile_number', mobileNumber);
    data.append('recaptcha_token', recaptchaToken);
    data.append('service_provider_key', serviceProviderKey);
    data.append('wants_to_subscribe', wantsToSubscribe ? 'true' : 'false');
    data.append('intended_cart_element_ids', JSON.stringify(intendedCartElementIds));
    if (subscribeGenderInterest != null) data.append('subscribe_gender_interest', subscribeGenderInterest);
    if (stripePaymentMethod != null) data.append('stripe_payment_method_id', stripePaymentMethod);
    data.append('save_stripe_payment_method', shouldSaveStripePaymentMethod);
    if (cartId != null) data.append('cart_id', cartId);
    if (savedAddressId != null) data.append('saved_address_id', savedAddressId);
    if (shippingMethodChoices != null) data.append('shipping_method_choices', JSON.stringify(shippingMethodChoices));
    const headers = idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : null;
    return _post('/api/place_order', data, cts, headers);
  }

  confirmStripePaymentIntent(paymentIntentId, cts) {
    const data = new FormData();
    data.append('payment_intent_id', paymentIntentId);
    return _post('/api/confirm_stripe_payment_intent', data, cts);
  }

  capturePaypalOrder(cts) {
    return _post('/api/capture_paypal_order', new FormData(), cts);
  }

  getPaypalOrderPlacementStatus(cts) {
    return _post('/api/get_paypal_order_placement_status', new FormData(), cts);
  }

  handleReferral(referral, cts) {
    const data = new FormData();
    data.append('referral', referral);
    return _post('/api/handle_referral', data, cts);
  }

  getAvailablePaymentMethods(tabbyParamsObject, cartId = null, cts) {
    const data = new FormData();
    if (tabbyParamsObject.firstName != null) data.append('first_name', tabbyParamsObject.firstName);
    if (tabbyParamsObject.lastName != null) data.append('last_name', tabbyParamsObject.lastName);
    if (tabbyParamsObject.email != null) data.append('email', tabbyParamsObject.email);
    if (tabbyParamsObject.mobileCountryCode != null) {
      data.append('mobile_country_code', tabbyParamsObject.mobileCountryCode);
    }
    if (tabbyParamsObject.mobileNumber != null) data.append('mobile_number', tabbyParamsObject.mobileNumber);
    if (cartId != null) data.append('cart_id', cartId);
    return _post('/api/get_available_payment_methods', data, cts);
  }

  getItemReviews(itemId, page, limit, sortPolicy, cts) {
    const data = new FormData();
    data.append('item_id', itemId);
    if (page) data.append('page', page);
    if (limit) data.append('limit', limit);
    data.append('sort_policy', sortPolicy);
    return _post('/api/get_item_reviews', data, cts);
  }

  getItemReview(reviewId, cts) {
    const data = new FormData();
    data.append('review_id', reviewId);
    return _post('/api/get_item_review', data, cts);
  }

  createItemReview(itemId, rating, reviewText, reviewTitle, cts) {
    const data = new FormData();
    data.append('item_id', itemId);
    data.append('rating', rating);
    if (reviewTitle) data.append('review_title', reviewTitle);
    if (reviewText) data.append('review_text', reviewText);
    return _post('/api/create_item_review', data, cts);
  }

  editItemReview(itemId, reviewId, rating, reviewText, reviewTitle, cts) {
    const data = new FormData();
    data.append('item_id', itemId);
    data.append('review_id', reviewId);
    if (rating) data.append('rating', rating);
    if (reviewTitle) data.append('review_title', reviewTitle);
    if (reviewText) data.append('review_text', reviewText);
    return _post('/api/edit_item_review', data, cts);
  }

  deleteItemReview(itemId, reviewId, cts) {
    const data = new FormData();
    data.append('item_id', itemId);
    data.append('review_id', reviewId);
    return _post('/api/delete_item_review', data, cts);
  }

  reportItemReview(reviewId, cts) {
    const data = new FormData();
    data.append('review_id', reviewId);
    return _post('/api/report_review', data, cts);
  }

  verifyTabbyPayment(cts) {
    const data = new FormData();
    return _post('/api/tabby_verification', data, cts);
  }

  isTabbySupportedForContext(itemId, cts) {
    const data = new FormData();
    if (itemId) data.append('item_id', itemId);
    return _post('/api/is_tabby_supported_for_context', data, cts);
  }

  prepareForCheckout(cartId = null, cts, shippingMethodChoices = null, city = null) {
    const data = new FormData();
    if (cartId != null) data.append('cart_id', cartId);
    if (shippingMethodChoices != null) data.append('shipping_method_choices', JSON.stringify(shippingMethodChoices));
    if (city) data.append('city', city);
    return _post('/api/prepare_for_checkout', data, cts);
  }

  getShippingOptions(cartId = null, city = null, cts) {
    const data = new FormData();
    if (cartId != null) data.append('cart_id', cartId);
    if (city) data.append('city', city);
    return _post('/api/get_shipping_options', data, cts);
  }

  getSellers(cts) {
    return _post('/api/get_all_sellers', new FormData(), cts);
  }

  getCategories(cts) {
    return _post('/api/get_categories', new FormData(), cts);
  }

  getParentCategories(cts) {
    return _post('/api/get_parent_categories', new FormData(), cts);
  }

  getMetaData(route, notFound, cts) {
    const data = new FormData();
    data.append('route', route);
    data.append('not_found', notFound ? 'true' : 'false');
    return _post('/api/seo_meta', data, cts);
  }

  getShoppingPageMetaData(primaryFilterKey, target, cts) {
    const data = new FormData();
    data.append('primary_filter_key', primaryFilterKey);
    data.append('target', target);
    return _post('/api/get_shopping_page_meta', data, cts);
  }

  queryItems(filterKeys, colorKeys, skip, limit, sortingPolicy, minPrice, maxPrice, priceRangeCurrency,
    publisherId = null, cts) {
    const data = new FormData();
    data.append('filter_keys_json', JSON.stringify(filterKeys));
    data.append('general_color_ids_json', JSON.stringify(colorKeys));
    if (skip != null) data.append('skip', skip);
    if (limit != null) data.append('limit', limit);
    if (minPrice != null) data.append('min_price', minPrice);
    if (maxPrice != null) data.append('max_price', maxPrice);
    if (publisherId != null) data.append('publisher_id', publisherId);
    if (priceRangeCurrency != null) data.append('price_range_currency', priceRangeCurrency);
    data.append('sorting_policy', sortingPolicy);
    return _post('/api/items', data, cts);
  }

  queryItemBySlug(slug, cts) {
    const data = new FormData();
    data.append('slug', slug);
    return _post('/api/query_item_by_slug', data, cts);
  }

  querySimilarItemsByItemId(itemId, cts) {
    const data = new FormData();
    data.append('item_id', itemId);
    return _post('/api/query_similar_items_by_item_id', data, cts);
  }

  queryItemsByIds(itemIds, cts) {
    if (Array.isArray(itemIds) && itemIds.length > QUERY_ITEMS_BY_IDS_BATCH_SIZE) {
      const itemIdChunks = chunk(itemIds, QUERY_ITEMS_BY_IDS_BATCH_SIZE);
      return mapWithConcurrency(
        itemIdChunks,
        QUERY_ITEMS_BY_IDS_MAX_CONCURRENCY,
        itemIdChunk => this.queryItemsByIds(itemIdChunk, cts)
      ).then(responses => {
        const failureResponse = responses.find(response => !response.data.success);
        if (failureResponse) return failureResponse;

        return {
          ...responses[0],
          data: {
            ...responses[0].data,
            items: responses.reduce((items, response) => items.concat(response.data.items), [])
          }
        };
      });
    }

    const data = new FormData();
    data.append('item_ids', JSON.stringify(itemIds));
    return _post('/api/query_items_by_ids', data, cts);
  }

  queryItemData(itemId, cts) {
    const data = new FormData();
    data.append('item_id', itemId);
    return _post('/api/query_item_by_id', data, cts);
  }

  queryShoppingFilters(baseFilterKeys, priceRangeCurrency, cts) {
    const data = new FormData();
    data.append('base_filter_keys_json', JSON.stringify(baseFilterKeys));
    if (priceRangeCurrency != null) data.append('price_range_currency', priceRangeCurrency);
    return _post('/api/query_shopping_filters', data, cts);
  }

  getCart(cartId = null, cts) {
    const data = new FormData();
    if (cartId != null) data.append('cart_id', cartId);
    return _post('/api/get_cart', data, cts);
  }

  addItemToCart(itemId, fabricId, extraOptionsData, sizeId, quantity, cartId = null, cts) {
    const data = new FormData();
    data.append('item_id', itemId);
    if (fabricId != null) data.append('fabric_id', fabricId);
    data.append('extra_options_data_json', JSON.stringify(extraOptionsData));
    if (sizeId) data.append('size_id', sizeId);
    const normalizedQuantity = quantity == null ? 1 : quantity;
    data.append('quantity', normalizedQuantity);
    if (cartId != null) data.append('cart_id', cartId);
    return _post('/api/add_item_to_cart', data, cts);
  }

  createEphemeralCart(cts) {
    return _post('/api/create_ephemeral_cart', new FormData(), cts);
  }

  removeFromCart(cartElementId, cts) {
    const data = new FormData();
    data.append('cart_element_id', cartElementId);
    return _post('/api/remove_from_cart', data, cts);
  }

  incCartItemQuantity(cartElementId, cts) {
    const data = new FormData();
    data.append('cart_element_id', cartElementId);
    return _post('/api/inc_quantity_in_cart', data, cts);
  }

  decCartItemQuantity(cartElementId, cts) {
    const data = new FormData();
    data.append('cart_element_id', cartElementId);
    return _post('/api/dec_quantity_in_cart', data, cts);
  }

  getFavorites(cts) {
    return _post('/api/get_favorites', new FormData(), cts);
  }

  addToFavorites(itemId, cts) {
    const data = new FormData();
    data.append('item_id', itemId);
    return _post('/api/add_to_favorites', data, cts);
  }

  removeFromFavorites(favoriteElementId, cts) {
    const data = new FormData();
    data.append('favorite_element_id', favoriteElementId);
    return _post('/api/remove_from_favorites', data, cts);
  }

  logItemInteraction(itemId, interaction, cts) {
    const data = new FormData();
    data.append('item_id', itemId);
    data.append('interaction', interaction);
    return _post('/api/log_item_interaction', data, cts);
  }

  getDynamicPage(pageKey, cts) {
    const data = new FormData();
    data.append('page_key', pageKey);
    return _post('/api/get_dynamic_page', data, cts);
  }

  getCurrentUserOrders(cts) {
    return _post('/api/get_my_orders', new FormData(), cts);
  }

  requestOrderCancellation(orderId, orderElementIds, reason, cts) {
    const data = new FormData();
    data.append('order_id', orderId);
    if (orderElementIds != null) {
      data.append('order_element_ids', JSON.stringify(orderElementIds));
    }
    if (reason != null) {
      data.append('reason', reason);
    }
    return _post('/api/request_order_cancellation', data, cts);
  }

  getCurrentGuestUserOrder(orderId, guestAccessCode, cts) {
    const data = new FormData();
    data.append('order_id', orderId);
    data.append('guest_access_code', guestAccessCode);
    return _post('/api/get_my_guest_order', data, cts);
  }

  getMyOrderDigitalAssetUrl(orderId, orderElementId, assetId, cts) {
    const data = new FormData();
    data.append('order_id', orderId);
    data.append('order_element_id', orderElementId);
    data.append('asset_id', assetId);
    return _post('/api/get_my_order_digital_asset_url', data, cts);
  }

  getOrderIdFromGuestAccessCode(guestAccessCode, cts) {
    const data = new FormData();
    data.append('guest_access_code', guestAccessCode);
    return _post('/api/get_guest_access_code_order_id', data, cts);
  }

  getDigitalOrderElementAssets(orderId, orderElementId, cts) {
    const data = new FormData();
    data.append('order_id', orderId);
    data.append('order_element_id', orderElementId);
    return _post('/api/get_digital_order_element_assets', data, cts);
  }

  getDigitalOrderElementAssetDownloadUrl(orderId, orderElementId, assetId, cts) {
    const data = new FormData();
    data.append('order_id', orderId);
    data.append('order_element_id', orderElementId);
    data.append('asset_id', assetId);
    return _post('/api/get_digital_order_element_asset_download_url', data, cts);
  }

  getStandardSizes(cts) {
    return _post('/api/get_standard_sizes', {}, cts);
  }

  getI18nSettings(cts) {
    return _postWithNoAuth('/api/get_i18n_settings', new FormData(), cts);
  }

  setShippingCountry(country, isForced, cts) {
    const data = new FormData();
    data.append('country', country);
    data.append('is_forced', isForced ? 'true' : 'false');
    return _post('/api/set_shipping_country', data, cts);
  }

  setCurrency(currency, cts) {
    const data = new FormData();
    data.append('currency', currency);
    return _post('/api/set_currency', data, cts);
  }

  setLocale(locale, cts) {
    const data = new FormData();
    data.append('locale', locale);
    return _post('/api/set_locale', data, cts);
  }

  getAllAnnouncements(cts) {
    return _postWithNoAuth('/api/get_all_announcements', new FormData(), cts);
  }

  getTheme(cts) {
    return _get('/api/theme', new FormData(), cts);
  }

  searchAlgolia(query, skip, limit, cts) {
    const data = new FormData();
    data.append('q', query);
    if (skip != null) data.append('skip', skip);
    if (limit != null) data.append('limit', limit);
    return _post('/api/search_v4', data, cts);
  }

  searchItems(query, skip, limit, i18nSettings, cts) {
    const data = new FormData();
    data.append('q', query);
    data.append('only_items', 'true');
    data.append('skip', skip);
    data.append('limit', limit);
    data.append('i18n_settings_json', JSON.stringify(i18nSettings));
    const config = { transformRequest: [removeSearchServiceHeaders] };
    if (cts) config.cancelToken = cts.token;
    return axios.post(process.env.REACT_APP_SEARCH_API_URL, data, config);
  }

  getAllWebPopups(cts) {
    return _post('/api/get_all_web_popups', new FormData(), cts);
  }

  getReturnPolicyById(returnPolicyId, cts) {
    const data = new FormData();
    data.append('return_policy_id', returnPolicyId);
    return _post('/api/get_return_policy_by_id', data, cts);
  }

  getReturnPolicyBySlug(slug, cts) {
    const data = new FormData();
    data.append('slug', slug);
    return _post('/api/get_return_policy_by_slug', data, cts);
  }

  getDeliveryPolicyById(deliveryPolicyId, itemId, cts) {
    const data = new FormData();
    if (itemId) data.append('item_id', itemId);
    data.append('delivery_policy_id', deliveryPolicyId);
    return _post('/api/get_delivery_policy_by_id', data, cts);
  }

  getDeliveryPolicyBySlug(slug, cts) {
    const data = new FormData();
    data.append('slug', slug);
    return _post('/api/get_delivery_policy_by_slug', data, cts);
  }

  getPlatformSettings(value, cts) {
    const data = new FormData();
    data.append('value', value);
    return _post('/api/get_platform_settings', data, cts);
  }

  getSignedUploadUrl(targetPath, extension, uploadIntent, cts) {
    const data = new FormData();
    data.append('target_path', targetPath);
    data.append('extension', extension);
    data.append('upload_intent', uploadIntent);
    return _post('/api/get_presignup_and_uuid_url', data, cts);
  }

  sessionInfoRequest(cts) {
    return _post('/api/session_info', new FormData(), cts);
  }

  getAllLiveStreams(cts) {
    return _post('/api/get_stream_feed', new FormData(), cts);
  }

  getLiveStream(liveStreamID, cts) {
    const data = new FormData();
    data.append('stream_id', liveStreamID);
    return _post('/api/get_stream_by_id', data, cts);
  }

  likeLiveStream(liveStreamID, cts) {
    const data = new FormData();
    data.append('stream_id', liveStreamID);
    return _post('/api/like_stream', data, cts);
  }

  shareLiveStream(liveStreamID, cts) {
    const data = new FormData();
    data.append('stream_id', liveStreamID);
    return _post('/api/share_stream', data, cts);
  }

  generateChatAccessToken(cts) {
    return _post('/api/generate_chat_access_token', new FormData(), cts);
  }

  joinLiveStreamChat(liveStreamId, cts) {
    const data = new FormData();
    data.append('stream_id', liveStreamId);
    return _post('/api/join_live_stream_chat', data, cts);
  }

  importSessionCart(cts) {
    return _post('/api/import_session_cart', new FormData(), cts);
  }

  getArticleBySlug(slug, cts) {
    const data = new FormData();
    data.append('slug', slug);
    return _post('/api/get_article_by_slug', data, cts);
  }

  sendEmailOtp(accessToken, cts) {
    const data = new FormData();
    data.append('access_token', accessToken);
    return _post('/api/post_sign_up/send_otp', data, cts);
  }

  verifyEmailOtp(accessToken, otp, cts) {
    const data = new FormData();
    data.append('access_token', accessToken);
    data.append('otp', otp);
    return _post('/api/post_sign_up/verify_otp', data, cts);
  }

  initiateChangeEmail(email, cts) {
    const data = new FormData();
    data.append('email', email);
    return _post('/api/change_email/initiate', data, cts);
  }

  cancelChangeEmail(verificationSessionId, cts) {
    const data = new FormData();
    data.append('verification_session_id', verificationSessionId);
    return _post('/api/change_email/cancel_verification', data, cts);
  }

  verifyChangeEmail(verificationSessionId, otp, cts) {
    const data = new FormData();
    data.append('verification_session_id', verificationSessionId);
    data.append('otp', otp);
    return _post('/api/change_email/verify', data, cts);
  }

  resendChangeEmailOtp(verificationSessionId, cts) {
    const data = new FormData();
    data.append('verification_session_id', verificationSessionId);
    return _post('/api/change_email/resend_otp', data, cts);
  }

  getMyMobileNumbers(paginationKey, limit, cts) {
    const data = new FormData();
    if (paginationKey != null) data.append('pagination_key', paginationKey);
    if (limit != null) data.append('limit', limit);
    return _post('/api/get_my_mobile_numbers', data, cts);
  }

  addMobileNumber(mobileCountryCode, mobileNumber, mobileAddressCountry, cts) {
    const data = new FormData();
    data.append('mobile_country_code', mobileCountryCode);
    data.append('mobile_number', mobileNumber);
    if (mobileAddressCountry) data.append('mobile_address_country', mobileAddressCountry);
    return _post('/api/add_mobile_number', data, cts);
  }

  deleteMobileNumber(userMobileNumberId, cts) {
    const data = new FormData();
    data.append('user_mobile_number_id', String(userMobileNumberId));
    return _post('/api/delete_mobile_number', data, cts);
  }

  sendMobileOtp(userMobileNumberId, countryCode, mobileNumber, cts) {
    const data = new FormData();
    data.append('user_mobile_number_id', String(userMobileNumberId));
    data.append('country_code', countryCode);
    data.append('mobile_number', mobileNumber);
    return _post('/api/mobile_number/send_otp', data, cts);
  }

  verifyMobileOtp(verificationSessionId, userMobileNumberId, otp, cts) {
    const data = new FormData();
    data.append('verification_session_id', verificationSessionId);
    data.append('user_mobile_number_id', String(userMobileNumberId));
    data.append('otp', otp);
    return _post('/api/mobile_number/verify_otp', data, cts);
  }

  resendMobileOtp(verificationSessionId, userMobileNumberId, cts) {
    const data = new FormData();
    data.append('verification_session_id', verificationSessionId);
    data.append('user_mobile_number_id', String(userMobileNumberId));
    return _post('/api/mobile_number/resend_otp', data, cts);
  }

  cancelMobileVerification(verificationSessionId, userMobileNumberId, cts) {
    const data = new FormData();
    data.append('verification_session_id', verificationSessionId);
    data.append('user_mobile_number_id', String(userMobileNumberId));
    return _post('/api/mobile_number/cancel_verification', data, cts);
  }

  removeStripePaymentMethod(paymentMethodId, cts) {
    const data = new FormData();
    data.append('payment_method_id', paymentMethodId);
    return _post('/api/remove_stripe_payment_method', data, cts);
  }

  confirmEmail(cts) {
    return _post('/api/confirm_email', new FormData(), cts);
  }

  submitSellerApplication(brandName, primaryContact, mobileCountryCode, mobileNumber, email, instagramProfileUrl,
    websiteType, websiteUrl, country, category, eligibilityBrandOwnerOrAuthorizedRepresentative,
    eligibilityReadyToSellInventory, eligibilityActiveInstagramOrOnlinePresence,
    eligibilityIncompleteApplicationsAutoRejected, businessMaturity, brandUniqueness,
    finalDeclarationInformationAccurate, finalDeclarationSubmissionNotGuaranteed,
    finalDeclarationAgreeToBeContacted, otherCategoryText, cts) {
    const data = new FormData();
    data.append('brand_name', brandName);
    data.append('primary_contact', primaryContact);
    data.append('mobile_country_code', mobileCountryCode);
    data.append('mobile_number', mobileNumber);
    data.append('email', email);
    data.append('instagram_profile_url', instagramProfileUrl);
    data.append('website_type', websiteType);
    if (websiteUrl) data.append('website_url', websiteUrl);
    data.append('country', country);
    data.append('category', category);
    data.append('eligibility_brand_owner_or_authorized_representative', eligibilityBrandOwnerOrAuthorizedRepresentative);
    data.append('eligibility_ready_to_sell_inventory', eligibilityReadyToSellInventory);
    data.append('eligibility_active_instagram_or_online_presence', eligibilityActiveInstagramOrOnlinePresence);
    data.append('eligibility_incomplete_applications_auto_rejected', eligibilityIncompleteApplicationsAutoRejected);
    data.append('business_maturity', businessMaturity);
    data.append('brand_uniqueness', brandUniqueness);
    data.append('final_declaration_information_accurate', finalDeclarationInformationAccurate);
    data.append('final_declaration_submission_not_guaranteed', finalDeclarationSubmissionNotGuaranteed);
    data.append('final_declaration_agree_to_be_contacted', finalDeclarationAgreeToBeContacted);
    if (otherCategoryText) data.append('other_category_text', otherCategoryText);
    return _post('/api/seller_application_submit', data, cts);
  }

  getAllTargetGenders(cts) {
    return _post('/api/get_all_target_genders', new FormData(), cts);
  }

  getTranslationFile(scope, locale, presentFileHash, cts) {
    const data = new FormData();
    data.append('scope', scope);
    data.append('locale', locale);
    data.append('present_file_hash', presentFileHash);
    return _post('/api/translation_file', data, cts);
  }

  getSmartAddressForm(stepIndex, stepsData, cts) {
    const data = new FormData();
    data.append('step_index', String(Math.max(0, stepIndex - 1)));
    data.append('steps_data', JSON.stringify(stepsData));
    return _post('/api/address/address_forms', data, cts);
  }

  getAllMySavedAddresses(cts) {
    return _post('/api/get_all_my_saved_addresses', new FormData(), cts);
  }

  createSavedAddress(d, cts) {
    const data = new FormData();
    data.append('address_country', d.address_country);
    data.append('label', d.label);
    data.append('is_default', d.is_default);
    data.append('first_name', d.first_name);
    data.append('last_name', d.last_name);
    data.append('mobile_number', d.mobile_number);
    if (d.mobile_country_code) data.append('mobile_country_code', d.mobile_country_code);
    data.append('special_instructions', d.special_instructions || '');
    if (d.address_line_0) data.append('address_line_0', d.address_line_0);
    if (d.address_line_1) data.append('address_line_1', d.address_line_1);
    if (d.address_line_2) data.append('address_line_2', d.address_line_2);
    if (d.city) data.append('city', d.city);
    if (d.district) data.append('district', d.district);
    if (d.governorate) data.append('governorate', d.governorate);
    if (d.po_box) data.append('po_box', d.po_box);
    if (d.postal_code) data.append('postal_code', d.postal_code);
    if (d.zip_code) data.append('zip_code', d.zip_code);
    if (d.province) data.append('province', d.province);
    if (d.state) data.append('state', d.state);
    if (d.street) data.append('street', d.street);
    if (d.floor) data.append('floor', d.floor);
    data.append('mobile_address_country', d.mobile_address_country);
    return _post('/api/create_user_saved_address', data, cts);
  }

  deleteSavedAddress(userAddressId, cts) {
    const data = new FormData();
    data.append('user_address_id', userAddressId);
    return _post('/api/delete_saved_address', data, cts);
  }

  setAddressAsPicked(userAddressId, cts) {
    const data = new FormData();
    data.append('user_address_id', userAddressId);
    return _post('/api/set_address_as_picked', data, cts);
  }

  setDefaultAddress(userAddressId, cts) {
    const data = new FormData();
    data.append('user_address_id', userAddressId);
    return _post('/api/set_default_address', data, cts);
  }

  fetchMyTickets(paginationKey, limit, cts) {
    const data = new FormData();
    if (paginationKey != null) data.append('pagination_key', paginationKey);
    if (limit != null) data.append('limit', limit);
    return _post('/api/tickets', data, cts);
  }

  fetchMyPersonalizedTickets(paginationKey, limit, status, cts) {
    const data = new FormData();
    if (paginationKey != null) data.append('pagination_key', paginationKey);
    if (limit != null) data.append('limit', limit);
    if (status) data.append('status', status);
    return _post('/api/tickets/personalized', data, cts);
  }

  getTicketOrderStatus(ticketProcessingToken, cts) {
    const data = new FormData();
    data.append('ticket_processing_token', ticketProcessingToken);
    return _post('/api/tickets/orders/status', data, cts);
  }

  issuePersonalizedTicket(ticketId, holderName, holderEmail, cts) {
    const data = new FormData();
    data.append('ticket_id', ticketId);
    data.append('holder_name', holderName);
    data.append('holder_email', holderEmail);
    return _post('/api/tickets/personalized/issue', data, cts);
  }

  fetchExperienceCatalog(currency, includeItems = true, includeDeletedHappenings = false, viewerTimezone = null, cts) {
    const data = new FormData();
    if (currency) data.append('currency', currency);
    data.append('include_items', includeItems ? 'true' : 'false');
    data.append('include_deleted_happenings', includeDeletedHappenings ? 'true' : 'false');
    if (viewerTimezone) data.append('viewer_timezone', viewerTimezone);
    return _post('/api/experiences/catalog/list', data, cts);
  }

  fetchExperienceDetail({
    experienceId = null,
    experienceSlug = null,
    currency = null,
    includeItems = true,
    viewerTimezone = null
  }, cts) {
    const data = new FormData();
    if (experienceId != null) data.append('experience_id', experienceId);
    if (experienceSlug != null) data.append('experience_slug', experienceSlug);
    if (currency) data.append('currency', currency);
    data.append('include_items', includeItems ? 'true' : 'false');
    if (viewerTimezone) data.append('viewer_timezone', viewerTimezone);
    return _post('/api/experiences/catalog/get', data, cts);
  }

  startThirdPartyCheckout(eventSlug, ticketClassId, quantity, cts, consentToDataSharing = false) {
    const data = new FormData();
    data.append('event_slug', eventSlug);
    if (ticketClassId != null) data.append('ticket_class_id', ticketClassId);
    if (quantity != null) data.append('quantity', quantity);
    data.append('consent_to_data_sharing', consentToDataSharing ? 'true' : 'false');
    return _post('/api/third_party_ticketing/checkout', data, cts);
  }

  startThirdPartyDirectCheckout(eventSlug, cts, consentToDataSharing = false) {
    const data = new FormData();
    data.append('event_slug', eventSlug);
    data.append('consent_to_data_sharing', consentToDataSharing ? 'true' : 'false');
    return _post('/api/third_party_ticketing/checkout/direct', data, cts);
  }

  getZendeskJWT(cts) {
    return _post('/api/zendesk/jwt', new FormData(), cts);
  }

  getLFClipById(lfclipId, cts) {
    const data = new FormData();
    data.append('lfclip_id', lfclipId);
    return _post('/api/get_lfclip_by_id', data, cts);
  }

  getLFClipsByIds(lfclipIds, cts) {
    const data = new FormData();
    data.append('lfclip_ids', JSON.stringify(lfclipIds));
    return _post('/api/get_lfclips_by_ids', data, cts);
  }

  getCreatorHubNewLFClips(paginationKey, limit, cts) {
    const data = new FormData();
    if (paginationKey != null) data.append('pagination_key', paginationKey);
    data.append('limit', String(limit));
    return _post('/api/get_creator_hub_new_lfclips', data, cts);
  }

  getPublisherMeta(publisherId = null, publisherHandle = null, cts) {
    const data = new FormData();
    if (publisherHandle != null) {
      data.append('publisher_handle', publisherHandle);
    } else {
      data.append('publisher_id', String(publisherId));
    }
    return _post('/api/get_publisher_meta', data, cts);
  }

  getPublisherJourneys(publisherId, includeClips, limit, clipsLimitPerJourney, cts) {
    const data = new FormData();
    data.append('user_id', publisherId);
    data.append('limit', limit);
    data.append('include_clips', includeClips.toString());
    data.append('clips_limit_per_journey', clipsLimitPerJourney);
    return _post('/api/user_journeys', data, cts);
  }

  getJourneyClips(userJourneyId, publisherId = null, paginationKey = null, direction = null, limit = null, cts) {
    const data = new FormData();
    data.append('user_journey_id', userJourneyId);
    if (publisherId != null) data.append('user_id', publisherId);
    if (paginationKey != null) data.append('pagination_key', paginationKey);
    if (direction != null) data.append('direction', direction);
    if (limit != null) data.append('limit', limit);
    return _post('/api/user_journeys/clips', data, cts);
  }

  getPublisherClips(publisherId, limit, cts) {
    const data = new FormData();
    data.append('publisher_id', publisherId);
    data.append('limit', limit);
    return _post('/api/get_lfclips', data, cts);
  }

  getUserTaggedItems(publihserId, limit, cts) {
    const data = new FormData();
    data.append('publisher_id', publihserId);
    data.append('limit', limit);
    return _post('/api/get_user_profile_tagged_items', data, cts);
  }

  getWallet(cts) {
    return _post('/api/my_wallet', new FormData(), cts);
  }

  getWalletHistory(limit, cursor, cts) {
    const data = new FormData();
    data.append('limit', limit);
    if (cursor) data.append('pagination_key', cursor);
    return _post('/api/get_wallet_history', data, cts);
  }
}

export default WebApiClient;
